// src/views/assembleias/AssembleiasList.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import AnexosUpload from "../../components/ui/AnexosUpload";
import assembleiaService  from "../../services/assembleiaService";
import condominiosService from "../../services/condominiosService";
import unidadesService    from "../../services/unidadesService";
import { useAuth }        from "../../contexts/AuthContext";
import { getApiOrigin }   from "../../services/apiBase";
import Banner from "../../components/ui/Banner";
import ConfirmModal from "../../components/ui/ConfirmModal";

const fmtD  = d => { try{ const s=(d||"").split("T")[0].split(" ")[0]; return new Date(s+"T12:00:00").toLocaleDateString("pt-BR",{weekday:"short",day:"2-digit",month:"short",year:"numeric"}); }catch{ return d||"—"; } };
/** Hora para exibição (lista) ou input type=time (HH:MM) — aceita string TIME do PG ou camelCase/minúsculas. */
const fmtH  = h => {
  if (h == null || h === "") return "";
  const s = typeof h === "string" ? h : String(h);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${String(m[1]).padStart(2,"0")}:${m[2]}`;
  return s.length >= 5 ? s.substring(0,5) : s;
};
const fmtDt = d => { try{ return new Date(d).toLocaleString("pt-BR"); }catch{ return d||"—"; } };

const STATUS_LABEL = { Rascunho:"Rascunho", Convocada:"Convocada", EmAndamento:"Em Andamento", Realizada:"Realizada", Encerrada:"Encerrada", Cancelada:"Cancelada" };
const STATUS_COLOR = { Rascunho:["#475569","#F1F5F9"], Convocada:["#1D4ED8","#EFF6FF"], EmAndamento:["#166534","#DCFCE7"], Realizada:["#7C3AED","#F5F3FF"], Encerrada:["#94A3B8","#F8FAFC"], Cancelada:["#991B1B","#FEE2E2"] };
const RES_COLOR    = { Aprovado:"#166534", Reprovado:"#991B1B", SemQuorum:"#92400E", Empate:"#1D4ED8" };

const EMPTY_FORM = { titulo:"", tpAssembleia:"Ordinaria", dtAssembleia:"", hrInicio:"19:00", hrFim:"", local:"", descricao:"", quorumMinPerc:"50" };
const EMPTY_PAUTA = { titulo:"", descricao:"", tpPauta:"Votacao", tpQuorum:"MaioriaSimples" };

export default function AssembleiasList() {
  const { condominioAtivo } = useAuth();
  const { podeEditar } = useAuth();
  const canEdit = podeEditar("assembleias");
  const [idCondo, setIdCondo]   = useState("");
  const [condominios, setCondominios] = useState([]);
  const [lista, setLista]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [resultados, setResultados] = useState(null);

  // Modal criar/editar
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData]   = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  // Modal pautas
  const [showPautas, setShowPautas] = useState(false);
  const [pautas, setPautas]         = useState([]);
  const [savingPautas, setSavingPautas] = useState(false);

  // Modal presença
  const [showPresenca, setShowPresenca] = useState(false);
  const [unidades, setUnidades]         = useState([]);
  const [buscaUnidade, setBuscaUnidade] = useState("");
  const [tpPresencaSelecionado, setTpPresencaSelecionado] = useState("Presencial");
  const [nmRepresentante, setNmRepresentante] = useState("");
  const [idUnidadeParaProcuracao, setIdUnidadeParaProcuracao] = useState(null);

  // Modal voto manual
  const [showVoto, setShowVoto]   = useState(false);
  const [votoData, setVotoData]           = useState({idPauta:"", idUnidade:"", voto:""});
  const [tpAnexoUpload, setTpAnexoUpload] = useState("Convocacao");
  const [anexos,        setAnexos]        = useState([]);
  const [abaDetalhe,    setAbaDetalhe]    = useState("pautas"); // pautas | presenca | documentos

  // Feedback / confirmação
  const [banner, setBanner] = useState(null);
  const showBanner = (tipo, msg) => { setBanner({ tipo, msg }); setTimeout(()=>setBanner(null), 7000); };
  const [confirmacao, setConfirmacao] = useState(null);

  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  useEffect(() => {
    condominiosService.listar({limit:100}).then(r=>setCondominios(r.data||[])).catch(()=>{});
  }, []);

  // Sync with navbar condominioAtivo — updates whenever user changes it
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const r = await assembleiaService.listar({idCondominio:idCondo});
      setLista(r.data||[]);
    } catch {}
    setLoading(false);
  }, [idCondo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Load anexos when assembleia is selected
  useEffect(() => {
    if (!selected?.id) { setAnexos([]); return; }
    assembleiaService.listarAnexos(selected.id)
      .then(r => setAnexos(r.data||[]))
      .catch(() => setAnexos([]));
  }, [selected?.id]);

  // Carrega unidades elegíveis (geraCondominio='Sim') quando muda o condomínio.
  // Garante que a aba "Presenças" mostre a lista mesmo sem abrir o modal.
  useEffect(() => {
    if (!idCondo) { setUnidades([]); return; }
    unidadesService.listar({idCondominio:idCondo, limit:500})
      .then(r => {
        const todas = r.data || [];
        // Apenas unidades que geram cota condominial (com direito a voto)
        const eleg = todas.filter(u => (u.geraCondominio || "Sim") === "Sim");
        // Ordena por bloco + unidade
        eleg.sort((a,b)=>{
          const ab=(a.bloco||"").localeCompare(b.bloco||"");
          if (ab) return ab;
          const an=parseFloat(a.unidade)||0, bn=parseFloat(b.unidade)||0;
          if (an!==bn) return an-bn;
          return String(a.unidade).localeCompare(String(b.unidade));
        });
        setUnidades(eleg);
      })
      .catch(() => setUnidades([]));
  }, [idCondo]);

  const abrirResultados = async (a) => {
    setSelected(a);
    try {
      const r = await assembleiaService.resultados(a.id);
      setResultados(r.data);
    } catch {}
  };

  const handleSalvar = async () => {
    if (!form.titulo?.trim()) return showBanner("warning", "Título obrigatório.");
    if (!form.dtAssembleia)   return showBanner("warning", "Data obrigatória.");
    setSaving(true);
    try {
      if (editData?.id) await assembleiaService.atualizar(editData.id, {...form, idCondominio:idCondo});
      else              await assembleiaService.criar({...form, idCondominio:idCondo});
      setShowModal(false);
      showBanner("success", editData?.id ? "Assembleia atualizada." : "Assembleia criada.");
      await carregar();
    } catch(e) { showBanner("error", e.message || "Erro ao salvar."); }
    setSaving(false);
  };

  const handleConvocar = (a) => {
    setConfirmacao({
      titulo: "Convocar assembleia",
      mensagem: (
        <div>
          <p>Convocar a assembleia <strong>"{a.titulo}"</strong>?</p>
          <p style={{fontSize:12,color:"var(--gray-400)",marginBottom:0}}>
            Serão enviadas notificações por e-mail e WhatsApp para todos os condôminos elegíveis.
          </p>
        </div>
      ),
      textoOk: "Convocar",
      onConfirm: async () => {
        try {
          const r = await assembleiaService.convocar(a.id);
          setConfirmacao(null);
          showBanner("success", r.message || "Assembleia convocada.");
          await carregar();
          if (selected?.id===a.id) abrirResultados({...a, status:"Convocada"});
        } catch(e) {
          setConfirmacao(null);
          showBanner("error", e.message || "Erro ao convocar.");
        }
      },
    });
  };

  const handleMudarStatus = (a, status) => {
    const cfg = {
      EmAndamento: { titulo:"Iniciar votação", mensagem:"Iniciar a votação agora?", textoOk:"Iniciar", perigo:false },
      Encerrada:   { titulo:"Encerrar assembleia", mensagem:"Encerrar a assembleia e calcular resultados? Tokens de votação serão invalidados.", textoOk:"Encerrar", perigo:false },
      Realizada:   { titulo:"Marcar como realizada", mensagem:"Marcar como realizada?", textoOk:"Confirmar", perigo:false },
      Cancelada:   { titulo:"Cancelar assembleia", mensagem:"Cancelar esta assembleia? Tokens de votação serão invalidados.", textoOk:"Cancelar assembleia", perigo:true },
    }[status] || { titulo:"Confirmar", mensagem:"Confirmar a alteração de status?", textoOk:"Confirmar", perigo:false };

    setConfirmacao({
      ...cfg,
      onConfirm: async () => {
        try {
          await assembleiaService.mudarStatus(a.id, status);
          setConfirmacao(null);
          showBanner("success", `Status alterado para ${status}.`);
          await carregar();
          if (selected?.id===a.id) abrirResultados({...a, status});
        } catch(e) {
          setConfirmacao(null);
          showBanner("error", e.message || "Erro ao mudar status.");
        }
      },
    });
  };

  const handleSalvarPautas = async () => {
    if (!selected) return;
    const invalidas = pautas.filter(p=>!p.titulo?.trim());
    if (invalidas.length) return showBanner("warning", "Todas as pautas precisam ter título.");
    setSavingPautas(true);
    try {
      await assembleiaService.salvarPautas(selected.id, {pautas: pautas.map((p,i)=>({...p,ordem:i+1}))});
      setShowPautas(false);
      showBanner("success", "Pautas salvas.");
      abrirResultados(selected);
    } catch(e) { showBanner("error", e.message || "Erro ao salvar pautas."); }
    setSavingPautas(false);
  };

  const handlePresenca = async (idUnidade, tp="Presencial", nmRepr=null) => {
    const aId = resultados?.id || selected?.id;
    if (!aId) return;
    if (tp === "Procuracao" && !nmRepr) {
      setIdUnidadeParaProcuracao(idUnidade);
      setNmRepresentante("");
      return;
    }
    try {
      await assembleiaService.registrarPresenca(aId, {
        idUnidade,
        tpPresenca: tp,
        nmRepresentante: nmRepr || null,
      });
      const r = await assembleiaService.resultados(aId);
      setResultados(r.data);
      setIdUnidadeParaProcuracao(null);
      setNmRepresentante("");
    } catch(e) { showBanner("error", e.message || "Erro ao registrar presença."); }
  };

  const handleRemoverPresenca = async (idUnidade) => {
    const aId = resultados?.id || selected?.id;
    if (!aId) return;
    try {
      await assembleiaService.removerPresenca(aId, idUnidade);
      const r = await assembleiaService.resultados(aId);
      setResultados(r.data);
    } catch(e) { showBanner("error", e.message || "Erro ao remover presença."); }
  };

  const handleVotoManual = async () => {
    if (!votoData.idPauta || !votoData.idUnidade || !votoData.voto) return showBanner("warning", "Preencha todos os campos.");
    try {
      await assembleiaService.votar(selected.id, votoData);
      setShowVoto(false);
      setVotoData({idPauta:"",idUnidade:"",voto:""});
      showBanner("success", "Voto registrado.");
      abrirResultados(selected);
    } catch(e) { showBanner("error", e.message || "Erro ao registrar voto."); }
  };

  const handleUploadAnexo = async (a) => {
    if (!selected?.id) return;
    try {
      const resp = await assembleiaService.uploadAnexo(selected.id, {...a, tpAnexo:tpAnexoUpload});
      if (resp?.data?.id) setAnexos(prev => [...prev, resp.data]);
      showBanner("success", "Anexo enviado.");
    } catch(e) { showBanner("error", e?.message || "Erro ao enviar anexo."); }
  };

  const handleRemoverAnexo = (idOrIdx) => {
    const anexo = anexos.find(x => x.id === idOrIdx) ?? anexos[idOrIdx];
    if (!anexo?.id) return;
    setConfirmacao({
      titulo: "Remover anexo",
      mensagem: `Remover "${anexo.nomeOriginal || anexo.nmArquivo || "este anexo"}"? Esta ação não pode ser desfeita.`,
      textoOk: "Remover",
      perigo: true,
      onConfirm: async () => {
        try {
          await assembleiaService.deletarAnexo(anexo.id);
          setAnexos(p => p.filter(a => a.id !== anexo.id));
          setConfirmacao(null);
          showBanner("success", "Anexo removido.");
        } catch(e) {
          setConfirmacao(null);
          showBanner("error", e?.message || "Erro ao remover.");
        }
      },
    });
  };

  const handleDownloadAnexo = (a) => {
    if (!a?.base64) return;
    const b64 = a.base64.startsWith("data:") ? a.base64
      : `data:${a.tpArquivo||"application/octet-stream"};base64,${a.base64}`;
    const lk = document.createElement("a");
    lk.href = b64; lk.download = a.nmArquivo || "anexo";
    document.body.appendChild(lk); lk.click(); document.body.removeChild(lk);
  };

  const handleGerarAta = async () => {
    if (!selected) return;
    try {
      const r = await assembleiaService.gerarAta(selected.id);
      // Use blob URL for reliable cross-browser print
      const blob = new Blob([r.html], { type: "text/html;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const w    = window.open(url, "_blank", "width=900,height=950");
      if (w) {
        // Trigger print after page loads
        w.onload = () => { setTimeout(() => w.print(), 500); };
      } else {
        // Popup blocked — fallback: download as HTML
        const a = document.createElement("a");
        a.href = url; a.download = `ata-assembleia-${selected.id}.html`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch(e) { showBanner("error", e?.message || "Erro ao gerar ata."); }
  };

  const abrirPautas = async (a) => {
    setSelected(a);
    try {
      const r = await assembleiaService.buscarPorId(a.id);
      setPautas(r.data.pautas||[]);
    } catch {}
    setShowPautas(true);
  };

  const abrirPresenca = async (a) => {
    setSelected(a);
    // Unidades já são carregadas via useEffect ao mudar idCondo
    await abrirResultados(a);
    setShowPresenca(true);
  };

  const aBase = getApiOrigin();

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{marginBottom:20}}>
        <div>
          <h1 className="page-title">
            <i className="bi bi-people-fill me-2" style={{color:"var(--primary)",fontSize:22}}/>
            Assembleias
          </h1>
          <p className="page-subtitle">Gestão de assembleias, votações e atas</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          
          <button className="btn btn-primary" onClick={()=>{setEditData(null);setForm(EMPTY_FORM);setShowModal(true);}} disabled={!idCondo || !canEdit}>
            <i className="bi bi-plus me-1"/>Nova Assembleia
          </button>
        </div>
      </div>

      {banner && (
        <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>
          {banner.msg}
        </Banner>
      )}

      <div style={{display:"grid",gridTemplateColumns:selected?"1fr 1.5fr":"1fr",gap:16}}>
        {/* Lista de assembleias */}
        <div style={{background:"var(--bg-surface)",borderRadius:12,border:"1px solid var(--gray-200)",overflow:"hidden"}}>
          {loading ? (
            <div style={{textAlign:"center",padding:40}}><span className="spinner-border text-primary"/></div>
          ) : !lista.length ? (
            <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
              <i className="bi bi-people" style={{fontSize:40,display:"block",marginBottom:8}}/>
              <p>Nenhuma assembleia cadastrada</p>
            </div>
          ) : (
            lista.map((a,i) => {
              const [cor, bg] = STATUS_COLOR[a.status]||["#475569","#F1F5F9"];
              return (
                <div key={a.id} onClick={()=>abrirResultados(a)}
                  style={{padding:"14px 16px",cursor:"pointer",
                    borderBottom:"1px solid var(--gray-100)",
                    background:selected?.id===a.id?"#EFF6FF":"#fff",
                    borderLeft:selected?.id===a.id?"3px solid var(--primary)":"3px solid transparent"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                    <div style={{fontWeight:600,fontSize:13.5,color:"var(--gray-800)"}}>{a.titulo}</div>
                    <span style={{background:bg,color:cor,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:99,flexShrink:0,marginLeft:8}}>
                      {STATUS_LABEL[a.status]}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:"var(--gray-500)",display:"flex",gap:12,flexWrap:"wrap"}}>
                    <span><i className="bi bi-calendar3 me-1"/>{fmtD(a.dtAssembleia)}</span>
                    <span><i className="bi bi-clock me-1"/>{fmtH(a.hrInicio ?? a.hrinicio)}</span>
                    {a.qtdPautas>0 && <span><i className="bi bi-list-ul me-1"/>{a.qtdPautas} pautas</span>}
                    {a.qtdPresentes>0 && <span><i className="bi bi-people me-1"/>{a.qtdPresentes} presentes</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Painel de detalhe / resultados */}
        {selected && resultados && (
          <div style={{background:"var(--bg-surface)",borderRadius:12,border:"1px solid var(--gray-200)",overflow:"hidden"}}>
            <div style={{padding:"14px 16px",borderBottom:"1px solid var(--gray-100)",
              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:"var(--gray-800)"}}>{resultados.titulo}</div>
                <div style={{fontSize:12,color:"var(--gray-500)"}}>{fmtD(resultados.dtAssembleia)} • {fmtH(resultados.hrInicio ?? resultados.hrinicio)} • {resultados.local||"Online"}</div>
              </div>
              <div style={{display:"flex",gap:6}}>
                {/* Ações por status */}
                {["Rascunho","Convocada"].includes(resultados.status) && (
                  <button className="btn btn-outline-secondary btn-sm" onClick={()=>{setEditData(resultados);setForm({...resultados, 
                    dtAssembleia: (resultados.dtAssembleia||"").split("T")[0].split(" ")[0],
                    hrInicio: fmtH(resultados.hrInicio ?? resultados.hrinicio) || "19:00",
                    hrFim: fmtH(resultados.hrFim ?? resultados.hrfim),
                    quorumMinPerc: resultados.quorumMinPerc != null ? String(resultados.quorumMinPerc) : (resultados.quorumminperc != null ? String(resultados.quorumminperc) : "50"),
                  });setShowModal(true);}}
                   disabled={!canEdit}>
                    <i className="bi bi-pencil"/>
                  </button>
                )}
                {["Rascunho","Cancelada"].includes(resultados.status) && canEdit && (
                  <button className="btn btn-outline-danger btn-sm"
                    onClick={()=>setConfirmacao({
                      titulo: "Excluir assembleia",
                      mensagem: `Excluir "${resultados.titulo}"? Esta ação não pode ser desfeita.`,
                      textoOk: "Excluir",
                      perigo: true,
                      onConfirm: async () => {
                        try {
                          await assembleiaService.deletar(resultados.id);
                          setLista(p=>p.filter(x=>x.id!==resultados.id));
                          setSelected(null); setResultados(null); setAnexos([]);
                          setConfirmacao(null);
                          showBanner("success", "Assembleia excluída.");
                        } catch(e){
                          setConfirmacao(null);
                          showBanner("error", e?.message||"Erro ao excluir.");
                        }
                      },
                    })}>
                    <i className="bi bi-trash me-1"/>Excluir
                  </button>
                )}
                {resultados.status==="Rascunho" && (
                  <button className="btn btn-outline-primary btn-sm" onClick={()=>abrirPautas(resultados)}>
                    <i className="bi bi-list-ul me-1"/>Pautas
                  </button>
                )}
                {resultados.status==="Rascunho" && (
                  <button className="btn btn-success btn-sm" onClick={()=>handleConvocar(resultados)}>
                    <i className="bi bi-send me-1"/>Convocar
                  </button>
                )}
                {resultados.status==="Convocada" && (
                  <button className="btn btn-primary btn-sm" onClick={()=>handleMudarStatus(resultados,"EmAndamento")}>
                    <i className="bi bi-play-fill me-1"/>Iniciar
                  </button>
                )}
                {["Convocada","EmAndamento"].includes(resultados.status) && canEdit && (
                  <button className="btn btn-outline-warning btn-sm" title="Reenviar link de votação para todas as unidades"
                    onClick={()=>setConfirmacao({
                      titulo: "Reenviar links de votação",
                      mensagem: "Reenviar o link de votação por e-mail e WhatsApp para todas as unidades convocadas?",
                      textoOk: "Reenviar",
                      onConfirm: async () => {
                        try {
                          const r = await assembleiaService.reenviarLink(resultados.id, null);
                          setConfirmacao(null);
                          showBanner("success", r.message || "Lembretes enviados.");
                        } catch(e){
                          setConfirmacao(null);
                          showBanner("error", e?.message||"Erro ao reenviar links.");
                        }
                      },
                    })}>
                    <i className="bi bi-send-arrow-up me-1"/>Reenviar Links
                  </button>
                )}
                {["Convocada","EmAndamento"].includes(resultados.status) && (
                  <button className="btn btn-outline-secondary btn-sm" onClick={()=>abrirPresenca(resultados)}>
                    <i className="bi bi-person-check me-1"/>Presença
                  </button>
                )}
                {resultados.status==="EmAndamento" && (<>
                  <button className="btn btn-outline-primary btn-sm" onClick={()=>setShowVoto(true)}>
                    <i className="bi bi-hand-index me-1"/>Voto Manual
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={()=>handleMudarStatus(resultados,"Encerrada")}>
                    <i className="bi bi-stop-fill me-1"/>Encerrar
                  </button>
                </>)}
                {["Realizada","Encerrada"].includes(resultados.status) && (<>
                  <button className="btn btn-outline-secondary btn-sm" onClick={()=>abrirPresenca(resultados)}>
                    <i className="bi bi-person-check me-1"/>Presenças
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={handleGerarAta}>
                    <i className="bi bi-file-text me-1"/>{resultados?.ataHtml ? "Reimprimir Ata" : "Gerar e Imprimir Ata"}
                  </button>
                  {resultados?.ataHtml && (
                    <button className="btn btn-outline-primary btn-sm"
                      onClick={()=>{
                        const blob = new Blob([resultados.ataHtml], {type:"text/html;charset=utf-8"});
                        const url  = URL.createObjectURL(blob);
                        const w    = window.open(url,"_blank","width=850,height=900");
                        setTimeout(()=>URL.revokeObjectURL(url),10000);
                      }}>
                      <i className="bi bi-printer me-1"/>Visualizar / Imprimir Ata
                    </button>
                  )}
                </>)}
                <button className="btn-close btn-sm ms-1" onClick={()=>{setSelected(null);setResultados(null);}}/>
              </div>
            </div>

            {/* Quórum + participação online */}
            <div style={{padding:"10px 16px",background:"var(--gray-50)",borderBottom:"1px solid var(--gray-100)",
              display:"flex",gap:20,fontSize:12.5,flexWrap:"wrap",alignItems:"center"}}>
              <span><strong>{resultados.qtdPresentes||0}</strong> / {resultados.totalUnidades||0} unidades presentes
                {resultados.totalUnidades>0 && (
                  <span style={{marginLeft:4,color:"var(--gray-400)"}}>
                    ({Math.round((resultados.qtdPresentes||0)/(resultados.totalUnidades||1)*100)}%)
                  </span>
                )}
              </span>
              <span>Quórum mínimo: <strong>{resultados.quorumMinPerc}%</strong></span>
              {resultados.qtdTokens>0 && (
                <span title="Tokens de votação online utilizados">
                  <i className="bi bi-link-45deg me-1"/>
                  <strong>{resultados.qtdTokensUsados||0}</strong>/{resultados.qtdTokens} link(s) utilizado(s)
                </span>
              )}
              {resultados.status==="Encerrada" && (
                <span style={{color:resultados.quorumAtingido?"#166534":"#991B1B",fontWeight:700,marginLeft:"auto"}}>
                  {resultados.quorumAtingido?"✅ Quórum atingido":"❌ Quórum não atingido"}
                </span>
              )}
            </div>

            {/* ── Abas do painel ── */}
            <div style={{display:"flex",borderBottom:"1px solid var(--gray-100)",background:"#FAFAFA"}}>
              {[
                {id:"pautas",     label:"Pautas",     icon:"bi-list-ul"},
                {id:"presenca",   label:"Presenças",  icon:"bi-person-check"},
                {id:"documentos", label:"Documentos", icon:"bi-paperclip"},
              ].map(t=>(
                <button key={t.id} type="button"
                  onClick={()=>setAbaDetalhe(t.id)}
                  style={{
                    padding:"10px 16px",border:"none",background:"none",cursor:"pointer",
                    fontSize:12,fontWeight:600,
                    color:abaDetalhe===t.id?"var(--primary)":"var(--gray-500)",
                    borderBottom:abaDetalhe===t.id?"2px solid var(--primary)":"2px solid transparent",
                    display:"flex",alignItems:"center",gap:6,
                  }}>
                  <i className={`bi ${t.icon}`}/>{t.label}
                </button>
              ))}
            </div>

            {/* Pautas */}
            {abaDetalhe==="pautas" && (
            <div style={{padding:16,overflowY:"auto",maxHeight:500}}>
              {(!resultados.pautas||resultados.pautas.length===0) ? (
                <div style={{textAlign:"center",padding:24,color:"var(--gray-400)"}}>
                  <i className="bi bi-list-ul" style={{fontSize:32,display:"block",marginBottom:8}}/>
                  <p style={{fontSize:13}}>Nenhuma pauta cadastrada</p>
                  {resultados.status==="Rascunho" && (
                    <button className="btn btn-primary btn-sm" onClick={()=>abrirPautas(resultados)}>
                      <i className="bi bi-plus me-1"/>Adicionar Pautas
                    </button>
                  )}
                </div>
              ) : (
                resultados.pautas.map((p,i) => {
                  const total = (p.votos?.Aprovado||0)+(p.votos?.Reprovado||0)+(p.votos?.Abstencao||0);
                  const pctAp = total>0?Math.round((p.votos.Aprovado/total)*100):0;
                  return (
                    <div key={p.id} style={{marginBottom:14,padding:"12px 14px",borderRadius:10,
                      border:"1px solid var(--gray-200)",
                      borderLeft:p.resultado?`4px solid ${RES_COLOR[p.resultado]||"#475569"}`:"4px solid var(--gray-200)"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                        <div style={{fontWeight:600,fontSize:13,color:"var(--gray-800)"}}>
                          {i+1}. {p.titulo}
                        </div>
                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                          {p.tpPauta!=="Informativo" && (
                            <span style={{fontSize:10,color:"var(--gray-400)"}}>{total} voto(s)</span>
                          )}
                          {p.resultado && (
                            <span style={{background:RES_COLOR[p.resultado]+"22",color:RES_COLOR[p.resultado],
                              fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:99}}>
                              {p.resultado}
                            </span>
                          )}
                        </div>
                      </div>
                      {p.descricao && <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:8}}>{p.descricao}</div>}
                      {p.tpPauta!=="Informativo" && total>0 && (
                        <div style={{marginTop:6}}>
                          {/* Barra visual dos votos */}
                          <div style={{display:"flex",height:8,borderRadius:99,overflow:"hidden",marginBottom:4}}>
                            {p.votos.Aprovado>0 && <div style={{flex:p.votos.Aprovado,background:"#22C55E"}}/>}
                            {p.votos.Reprovado>0 && <div style={{flex:p.votos.Reprovado,background:"#EF4444"}}/>}
                            {p.votos.Abstencao>0 && <div style={{flex:p.votos.Abstencao,background:"#94A3B8"}}/>}
                          </div>
                          <div style={{display:"flex",gap:12,fontSize:11,color:"var(--gray-500)"}}>
                            <span style={{color:"#166534"}}>✅ {p.votos.Aprovado} Aprovados ({pctAp}%)</span>
                            <span style={{color:"#991B1B"}}>❌ {p.votos.Reprovado} Reprovados</span>
                            <span>⚪ {p.votos.Abstencao} Abstenções</span>
                          </div>
                        </div>
                      )}
                      {p.tpPauta!=="Informativo" && ["EmAndamento","Convocada"].includes(resultados.status) && (
                        <div style={{marginTop:8}}>
                          <span style={{fontSize:11,color:"var(--gray-400)"}}>
                            <i className="bi bi-link-45deg me-1"/>
                            Link: {aBase}/votar?token=... (gerado por unidade ao convocar)
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            )} {/* end abaDetalhe==="pautas" */}

            {/* ── Aba Presenças ── */}
            {abaDetalhe==="presenca" && (
              <div style={{padding:16,overflowY:"auto",maxHeight:500}}>
                {(() => {
                  const presencas = resultados.presencas || [];
                  const totElegiveis = unidades.length || 0;
                  const totPres = presencas.length;
                  const qtdPresencial  = presencas.filter(p=>p.tpPresenca==="Presencial").length;
                  const qtdOnline      = presencas.filter(p=>p.tpPresenca==="Online").length;
                  const qtdProcuracao  = presencas.filter(p=>p.tpPresenca==="Procuracao"||p.tpPresenca==="Procuração").length;
                  const perc = totElegiveis ? Math.round((totPres/totElegiveis)*100) : 0;
                  return (
                    <div style={{marginBottom:14,padding:"12px 14px",background:"var(--bg-surface-alt)",border:"1px solid var(--border-color)",borderRadius:8}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <span style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>
                          <i className="bi bi-people-fill me-1"/>Resumo de Presença
                        </span>
                        <span style={{fontSize:13,fontWeight:700,color: perc>=50?"#15803D":"#B45309"}}>
                          {totPres} / {totElegiveis} ({perc}%)
                        </span>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                        <div style={{background:"var(--bg-success-soft)",border:"1px solid #BBF7D0",borderRadius:6,padding:"6px 10px",textAlign:"center"}}>
                          <div style={{fontSize:11,color:"#166534",fontWeight:600}}>Presencial</div>
                          <div style={{fontSize:18,fontWeight:700,color:"#166534"}}>{qtdPresencial}</div>
                        </div>
                        <div style={{background:"var(--bg-highlight)",border:"1px solid #BFDBFE",borderRadius:6,padding:"6px 10px",textAlign:"center"}}>
                          <div style={{fontSize:11,color:"#1E40AF",fontWeight:600}}>Online</div>
                          <div style={{fontSize:18,fontWeight:700,color:"#1E40AF"}}>{qtdOnline}</div>
                        </div>
                        <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:6,padding:"6px 10px",textAlign:"center"}}>
                          <div style={{fontSize:11,color:"#92400E",fontWeight:600}}>Procuração</div>
                          <div style={{fontSize:18,fontWeight:700,color:"#92400E"}}>{qtdProcuracao}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {unidades.length===0 ? (
                    <div style={{padding:24,textAlign:"center",color:"var(--gray-400)",fontSize:13}}>
                      Nenhuma unidade elegível encontrada para este condomínio.
                    </div>
                  ) : unidades.map(u=>{
                    const presente = resultados.presencas?.find(p=>
                      String(p.idUnidade)===String(u.id)||String(p.id)===String(u.id));
                    const isProcInput = idUnidadeParaProcuracao===u.id;
                    return (
                      <div key={u.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                        padding:"8px 12px",background:presente?"#F0FDF4":"#F8FAFC",
                        borderRadius:8,border:`1px solid ${presente?"#BBF7D0":"#E2E8F0"}`,gap:8,flexWrap:"wrap"}}>
                        <div style={{flex:"1 1 220px",minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--text-primary)"}}>
                            Ap. {u.unidade}{u.bloco?`/Bl.${u.bloco}`:""}
                          </div>
                          {(presente?.nmCondomino || u.nmProprietario) && (
                            <div style={{fontSize:11,color:"var(--gray-500)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {presente?.nmCondomino || u.nmProprietario}
                              {presente?.nmRepresentante && (
                                <span style={{marginLeft:6,color:"#92400E",fontWeight:600}}>
                                  · Repr.: {presente.nmRepresentante}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {presente ? (
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:11,fontWeight:600,padding:"3px 8px",borderRadius:10,
                              background: presente.tpPresenca==="Online"?"#DBEAFE":presente.tpPresenca==="Procuracao"||presente.tpPresenca==="Procuração"?"#FEF3C7":"#DCFCE7",
                              color: presente.tpPresenca==="Online"?"#1E40AF":presente.tpPresenca==="Procuracao"||presente.tpPresenca==="Procuração"?"#92400E":"#166534"}}>
                              ✅ {presente.tpPresenca}
                            </span>
                            {canEdit && (
                              <button className="btn btn-outline-danger btn-sm btn-icon"
                                onClick={()=>handleRemoverPresenca(u.id)} title="Remover presença">
                                <i className="bi bi-x"/>
                              </button>
                            )}
                          </div>
                        ) : canEdit && (
                          isProcInput ? (
                            <div style={{display:"flex",gap:4,alignItems:"center",flex:"1 1 280px"}}>
                              <input className="form-control form-control-sm" autoFocus
                                placeholder="Nome do procurador..."
                                value={nmRepresentante}
                                onChange={e=>setNmRepresentante(e.target.value)}
                                onKeyDown={e=>{ if (e.key==="Enter" && nmRepresentante.trim()) handlePresenca(u.id,"Procuracao",nmRepresentante.trim()); }}
                                style={{fontSize:12}}/>
                              <button className="btn btn-success btn-sm" disabled={!nmRepresentante.trim()}
                                onClick={()=>handlePresenca(u.id,"Procuracao",nmRepresentante.trim())}>
                                <i className="bi bi-check"/>
                              </button>
                              <button className="btn btn-outline-secondary btn-sm"
                                onClick={()=>{setIdUnidadeParaProcuracao(null);setNmRepresentante("");}}>
                                <i className="bi bi-x"/>
                              </button>
                            </div>
                          ) : (
                            <div style={{display:"flex",gap:4}}>
                              {[
                                {v:"Presencial",l:"Presencial"},
                                {v:"Online",l:"Online"},
                                {v:"Procuracao",l:"Procuração"},
                              ].map(tp=>(
                                <button key={tp.v} className="btn btn-outline-secondary btn-sm"
                                  style={{fontSize:11,padding:"2px 8px"}}
                                  onClick={()=>handlePresenca(u.id,tp.v)}>
                                  {tp.l}
                                </button>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Aba Documentos ── */}
            {abaDetalhe==="documentos" && (
              <div style={{padding:16,overflowY:"auto",maxHeight:500}}>
                {/* Seletor de tipo */}
                <div style={{marginBottom:12,display:"flex",gap:6,flexWrap:"wrap"}}>
                  {[{v:"Convocacao",l:"📋 Convocação"},{v:"Ata",l:"📄 Ata"},{v:"Documento",l:"📎 Documento"}].map(tp=>(
                    <button key={tp.v} type="button"
                      className={`btn btn-sm ${tpAnexoUpload===tp.v?"btn-primary":"btn-outline-secondary"}`}
                      style={{fontSize:12}}
                      onClick={()=>setTpAnexoUpload(tp.v)}>
                      {tp.l}
                    </button>
                  ))}
                </div>
                <AnexosUpload
                  anexos={anexos}
                  onAdd={handleUploadAnexo}
                  onRemove={(idOrIdx)=>{ const a=anexos.find(x=>x.id===idOrIdx)??anexos[idOrIdx]; if(a?.id) handleRemoverAnexo(a.id); }}
                  onDownload={(a)=>handleDownloadAnexo(a)}
                  label="Adicionar documento"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  maxSizeMB={5}
                />
                {anexos.length>0 && (
                  <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:4}}>
                    {anexos.map((a,i)=>(
                      <span key={i} style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600,
                        background:a.tpAnexo==="Ata"?"#D1FAE5":a.tpAnexo==="Convocacao"?"#DBEAFE":"#F1F5F9",
                        color:a.tpAnexo==="Ata"?"#065F46":a.tpAnexo==="Convocacao"?"#1E40AF":"#475569"}}>
                        {a.tpAnexo||"Documento"}: {a.nomeOriginal||a.nmArquivo}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </div>

      {/* ── Modal Criar/Editar ── */}
      {showModal && (
        <div className="modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="modal-box" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-people-fill me-2"/>{editData?"Editar":"Nova"} Assembleia</h5>
              <button className="btn-close" onClick={()=>setShowModal(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Título *</label>
                  <input className="form-control" value={form.titulo} onChange={e=>setF("titulo",e.target.value)} placeholder="Ex: AGO 2026 — Aprovação do Orçamento"/>
                </div>
                <div className="col-6">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={form.tpAssembleia} onChange={e=>setF("tpAssembleia",e.target.value)}>
                    <option value="Ordinaria">Ordinária (AGO)</option>
                    <option value="Extraordinaria">Extraordinária (AGE)</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label">Quórum mínimo (%)</label>
                  <input type="number" className="form-control" value={form.quorumMinPerc} min="0" max="100" onChange={e=>setF("quorumMinPerc",e.target.value)}/>
                </div>
                <div className="col-4">
                  <label className="form-label">Data *</label>
                  <input type="date" className="form-control" value={form.dtAssembleia} onChange={e=>setF("dtAssembleia",e.target.value)}/>
                </div>
                <div className="col-4">
                  <label className="form-label">Hora Início</label>
                  <input type="time" className="form-control" value={form.hrInicio} onChange={e=>setF("hrInicio",e.target.value)}/>
                </div>
                <div className="col-4">
                  <label className="form-label">Hora Fim</label>
                  <input type="time" className="form-control" value={form.hrFim} onChange={e=>setF("hrFim",e.target.value)}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Local</label>
                  <input className="form-control" value={form.local} onChange={e=>setF("local",e.target.value)} placeholder="Salão de Festas / Online"/>
                </div>
                <div className="col-12">
                  <label className="form-label">Descrição</label>
                  <textarea className="form-control" rows={3} value={form.descricao} onChange={e=>setF("descricao",e.target.value)}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvar} disabled={saving}>
                {saving?<span className="spinner-border spinner-border-sm"/>:<i className="bi bi-check-circle me-1"/>}
                {editData?"Salvar":"Criar Assembleia"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Pautas ── */}
      {showPautas && (
        <div className="modal-overlay" onClick={()=>setShowPautas(false)}>
          <div className="modal-box" style={{maxWidth:600}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-list-ul me-2"/>Pautas — {selected?.titulo}</h5>
              <button className="btn-close" onClick={()=>setShowPautas(false)}/>
            </div>
            <div className="modal-body" style={{maxHeight:440,overflowY:"auto"}}>
              {pautas.map((p,i)=>(
                <div key={i} style={{padding:"12px 14px",marginBottom:10,background:"var(--gray-50)",borderRadius:8,border:"1px solid var(--gray-200)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <span style={{fontWeight:600,fontSize:13,color:"var(--gray-600)"}}>Pauta {i+1}</span>
                    <button className="btn btn-outline-danger btn-sm btn-icon" onClick={()=>setPautas(p=>p.filter((_,j)=>j!==i))}>
                      <i className="bi bi-trash3"/>
                    </button>
                  </div>
                  <div className="row g-2">
                    <div className="col-12">
                      <input className="form-control form-control-sm" placeholder="Título da pauta *"
                        value={p.titulo} onChange={e=>{const n=[...pautas];n[i]={...n[i],titulo:e.target.value};setPautas(n);}}/>
                    </div>
                    <div className="col-12">
                      <textarea className="form-control form-control-sm" rows={2} placeholder="Descrição (opcional)"
                        value={p.descricao||""} onChange={e=>{const n=[...pautas];n[i]={...n[i],descricao:e.target.value};setPautas(n);}}/>
                    </div>
                    <div className="col-6">
                      <select className="form-select form-select-sm" value={p.tpPauta}
                        onChange={e=>{const n=[...pautas];n[i]={...n[i],tpPauta:e.target.value};setPautas(n);}}>
                        <option value="Votacao">Votação</option>
                        <option value="Informativo">Informativo (sem voto)</option>
                        <option value="Eleicao">Eleição</option>
                      </select>
                    </div>
                    {p.tpPauta!=="Informativo" && (
                      <div className="col-6">
                        <select className="form-select form-select-sm" value={p.tpQuorum}
                          onChange={e=>{const n=[...pautas];n[i]={...n[i],tpQuorum:e.target.value};setPautas(n);}}>
                          <option value="MaioriaSimples">Maioria Simples (&gt;50%)</option>
                          <option value="MaioriaQualificada">Maioria Qualificada (75%)</option>
                          <option value="Unanimidade">Unanimidade (100%)</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <button className="btn btn-outline-primary w-100" onClick={()=>setPautas(p=>[...p,{...EMPTY_PAUTA}])}>
                <i className="bi bi-plus me-1"/>Adicionar Pauta
              </button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowPautas(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvarPautas} disabled={savingPautas}>
                {savingPautas?<span className="spinner-border spinner-border-sm"/>:<i className="bi bi-check-circle me-1"/>}
                Salvar Pautas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Presença ── */}
      {showPresenca && resultados && (
        <div className="modal-overlay" onClick={()=>setShowPresenca(false)}>
          <div className="modal-box" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-person-check me-2"/>Registrar Presença</h5>
              <button className="btn-close" onClick={()=>setShowPresenca(false)}/>
            </div>
            <div className="modal-body">
              <div style={{marginBottom:10}}>
                <input className="form-control form-control-sm" placeholder="Buscar unidade..."
                  value={buscaUnidade} onChange={e=>setBuscaUnidade(e.target.value)}/>
              </div>
              <div style={{maxHeight:380,overflowY:"auto"}}>
                {unidades.filter(u=>!buscaUnidade||(`${u.unidade}${u.bloco||""}${u.nmProprietario||""}`).toLowerCase().includes(buscaUnidade.toLowerCase())).map(u=>{
                  const presente = resultados.presencas?.find(p=>String(p.idUnidade)===String(u.id)||String(p.id)===String(u.id));
                  const isProcInput = idUnidadeParaProcuracao===u.id;
                  return (
                    <div key={u.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      padding:"7px 10px",borderBottom:"1px solid var(--gray-100)",
                      background:presente?"#F0FDF4":"#fff",gap:6,flexWrap:"wrap"}}>
                      <div style={{flex:"1 1 140px",minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600}}>{u.unidade}{u.bloco?`/Bl.${u.bloco}`:""}</div>
                        {(presente?.nmCondomino || u.nmProprietario) && (
                          <div style={{fontSize:11,color:"var(--gray-500)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {presente?.nmCondomino || u.nmProprietario}
                            {presente?.nmRepresentante && <span style={{marginLeft:4,color:"#92400E"}}>· {presente.nmRepresentante}</span>}
                          </div>
                        )}
                      </div>
                      {presente ? (
                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                          <span style={{fontSize:11,color:"#166534",fontWeight:600}}>✅ {presente.tpPresenca}</span>
                          <button className="btn btn-outline-danger btn-sm btn-icon" onClick={()=>handleRemoverPresenca(u.id)}>
                            <i className="bi bi-x"/>
                          </button>
                        </div>
                      ) : (
                        isProcInput ? (
                          <div style={{display:"flex",gap:4,alignItems:"center",flex:"1 1 200px"}}>
                            <input className="form-control form-control-sm" autoFocus
                              placeholder="Nome do procurador..."
                              value={nmRepresentante}
                              onChange={e=>setNmRepresentante(e.target.value)}
                              onKeyDown={e=>{ if (e.key==="Enter" && nmRepresentante.trim()) handlePresenca(u.id,"Procuracao",nmRepresentante.trim()); }}
                              style={{fontSize:11}}/>
                            <button className="btn btn-success btn-sm" disabled={!nmRepresentante.trim()}
                              onClick={()=>handlePresenca(u.id,"Procuracao",nmRepresentante.trim())}>
                              <i className="bi bi-check"/>
                            </button>
                            <button className="btn btn-outline-secondary btn-sm"
                              onClick={()=>{setIdUnidadeParaProcuracao(null);setNmRepresentante("");}}>
                              <i className="bi bi-x"/>
                            </button>
                          </div>
                        ) : (
                          <div style={{display:"flex",gap:4}}>
                            {[
                              {v:"Presencial",l:"Presencial"},
                              {v:"Online",l:"Online"},
                              {v:"Procuracao",l:"Procuração"},
                            ].map(tp=>(
                              <button key={tp.v} className="btn btn-outline-success btn-sm" style={{fontSize:10,padding:"2px 8px"}}
                                onClick={()=>handlePresenca(u.id, tp.v)}>
                                {tp.l}
                              </button>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <div style={{fontSize:12,color:"var(--gray-500)"}}>
                {resultados.presencas?.length||0} presente(s) de {unidades.length} unidades
              </div>
              <button className="btn btn-outline-secondary" onClick={()=>setShowPresenca(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Voto Manual ── */}
      {showVoto && resultados && (
        <div className="modal-overlay" onClick={()=>setShowVoto(false)}>
          <div className="modal-box" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-hand-index me-2"/>Registrar Voto Manual</h5>
              <button className="btn-close" onClick={()=>setShowVoto(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Pauta</label>
                  <select className="form-select" value={votoData.idPauta} onChange={e=>setVotoData(p=>({...p,idPauta:e.target.value}))}>
                    <option value="">Selecione</option>
                    {(resultados.pautas||[]).filter(p=>p.tpPauta!=="Informativo").map(p=>(
                      <option key={p.id} value={p.id}>{p.ordem}. {p.titulo}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Unidade</label>
                  <select className="form-select" value={votoData.idUnidade} onChange={e=>setVotoData(p=>({...p,idUnidade:e.target.value}))}>
                    <option value="">Selecione</option>
                    {unidades.map(u=><option key={u.id} value={u.id}>{u.unidade}{u.bloco?`/Bl.${u.bloco}`:""}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Voto</label>
                  <div style={{display:"flex",gap:8}}>
                    {["Aprovado","Reprovado","Abstencao"].map(v=>(
                      <button key={v} onClick={()=>setVotoData(p=>({...p,voto:v}))}
                        style={{flex:1,padding:"8px 0",border:`2px solid ${votoData.voto===v?(v==="Aprovado"?"#166534":v==="Reprovado"?"#991B1B":"#64748B"):"var(--gray-200)"}`,
                          borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:12,
                          background:votoData.voto===v?(v==="Aprovado"?"#DCFCE7":v==="Reprovado"?"#FEE2E2":"#F1F5F9"):"#fff",
                          color:v==="Aprovado"?"#166534":v==="Reprovado"?"#991B1B":"#64748B"}}>
                        {v==="Aprovado"?"✅ Aprovado":v==="Reprovado"?"❌ Reprovado":"⚪ Abstenção"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowVoto(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleVotoManual}>
                <i className="bi bi-check-circle me-1"/>Registrar Voto
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          textoOk={confirmacao.textoOk || "Confirmar"}
          textoCancelar={confirmacao.textoCancelar || "Cancelar"}
          perigo={!!confirmacao.perigo}
          onConfirm={confirmacao.onConfirm}
          onCancel={()=>setConfirmacao(null)}
        />
      )}

    </div>
  );
}
